import { Image, StyleSheet, Text, View } from '@react-pdf/renderer';
import { Style } from '@react-pdf/types';
import { getColorObjectsStyleByRecord } from 'PDF/helpers/documentStyle';

export interface IFeaturesTableImageInfo {
  imgSrc: string;
}

export interface IFeaturesTableRowItem {
  id: string;
  heading: string;
  items: (string | IFeaturesTableImageInfo)[];
}

interface IFeaturesTableProps {
  rows: IFeaturesTableRowItem[];
  noCapturedFirstColumn?: boolean;
}

const colorIndexes = {
  green: [2, 7],
  blue: [2, 1],
  red: [2, 1],
  blueGreen: [2, 1],
  blueRed: [2, 1],
  blueYellow: [2, 5],
};

export const FeaturesTable = ({ rows, noCapturedFirstColumn = false }: IFeaturesTableProps) => {
  const [rowWrapColor, headingWrapColor] = getColorObjectsStyleByRecord(colorIndexes, [
    'backgroundColor',
    'backgroundColor',
  ]);

  return (
    <>
      {rows.map(({ heading, items, id }, rowIndex) => {
        const isLast = rowIndex === rows.length - 1;
        const ceilStyle = {
          paddingTop: rowIndex === 0 ? '10px' : '0px',
          paddingBottom: isLast ? '10px' : '0px',
        };
        const lastCeilStyle = {
          borderBottom: isLast ? 'none' : '0.5px dotted #77787C',
        };
        return (
          <View key={id} wrap={false} style={styles.rowWrap}>
            <View style={[styles.headingWrap, ceilStyle, headingWrapColor as Style]}>
              <View style={styles.headingCeil}>
                <Text style={styles.heading}>{heading}</Text>
              </View>
            </View>
            <View style={[styles.infoWrap, ceilStyle, rowWrapColor as Style]}>
              {items.map((item, index) => (
                <View
                  key={index}
                  style={[
                    styles.infoCeil,
                    {
                      backgroundColor: index === 0 && !noCapturedFirstColumn ? '#fff' : 'transparent',
                      width: `${100 / items.length}%`,
                      padding: index === 0 ? '0 10px' : '0 0 0 10px',
                    },
                  ]}
                >
                  <View style={[styles.info, lastCeilStyle]}>
                    {typeof item === 'string' ? (
                      <Text style={{ ...styles.text, fontWeight: rowIndex === 0 ? 400 : 600 }}>{item}</Text>
                    ) : (
                      !!item.imgSrc && <Image src={item.imgSrc} style={styles.img} />
                    )}
                  </View>
                </View>
              ))}
            </View>
          </View>
        );
      })}
    </>
  );
};

const styles = StyleSheet.create({
  rowWrap: {
    display: 'flex',
    flexDirection: 'row',
    marginTop: '-0.3px',
  },
  headingWrap: {
    display: 'flex',
    flexDirection: 'row',
    height: '100%',
    paddingLeft: '12px',
    paddingRight: '12px',
    width: '170px',
    marginRight: '5.45px',
  },
  headingCeil: {
    display: 'flex',
    flexDirection: 'row',
    height: '100%',
    width: '100%',
    borderBottom: '0.3px dotted #FFFFFF',
    paddingBottom: '5px',
    paddingTop: '4.41px',
    marginTop: '-1px',
  },
  heading: {
    fontFamily: 'Noto',
    fontWeight: 600,
    fontSize: '9px',
    color: '#fff',
  },
  infoWrap: {
    padding: '10px 10.27px 0 8.78px',
    display: 'flex',
    flexDirection: 'row',
    height: '100%',
    width: '100%',
  },
  infoCeil: {
    display: 'flex',
    flexDirection: 'row',
    height: '100%',
    width: '100%',
    color: '#212121',
  },
  info: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    height: '100%',
    width: '100%',
    paddingBottom: '5px',
    paddingTop: '4.41px',
  },
  text: {
    fontFamily: 'Noto',
    fontWeight: 400,
    fontSize: '9px',
  },
  img: {
    width: '100px',
    objectFit: 'contain',
    objectPositionX: 0,
    objectPositionY: 0,
  },
});
